import { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Select from 'react-select';
import './Create.css';


const SubContractCreate = () => {
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    // Logout if token is expired
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        Name: '',
        ContactNumber: '',
        StateId: '',
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
    });

 

    const submitData = async () => {
        // Clear previous messages
        setErrorMessage("");
        setSuccessMessage("");

        // Validation
        if (!formData.Name || !formData.Name.trim()) {
            setErrorMessage('Name is required.');
            return;
        }

        if (!formData.ContactNumber || !formData.ContactNumber.trim()) {
            setErrorMessage('Contact Number is required.');
            return;
        }

        if (!formData.StateId || formData.StateId === '' || formData.StateId === 0) {
            console.log('State validation failed. StateId:', formData.StateId);
            console.log('Full formData:', formData);
            setErrorMessage('The State field is required.');
            return;
        }

        setIsLoading(true);

        try {
            // Fetch existing data
            const SubContacters = await fetchData('SubContractAPI', `/${companyId}`, token);
            console.log('Existing SubContact:', SubContacters);

            if (
                SubContacters.response_data &&
                Array.isArray(SubContacters.response_data) &&
                SubContacters.response_data.some(
                    (item) => item.Name === formData.Name
                )
            ) {
                setErrorMessage('SubContacter with the same Name already exists.');
                setIsLoading(false);
                return;
            }

            if (
                SubContacters.response_data &&
                Array.isArray(SubContacters.response_data) &&
                SubContacters.response_data.some(
                    (item) => item.ContactNumber === formData.ContactNumber
                )
            ) {
                setErrorMessage('SubContacter with the same Contact Number already exists.');
                setIsLoading(false);
                return;
            }
           
            // Prepare data for submission with latest values
            const dataToSubmit = {
                CompanyId: parseInt(companyId),
                Name: formData.Name.trim(),
                ContactNumber: formData.ContactNumber.trim(),
                StateId: parseInt(formData.StateId),
                PurchaseOrderId: null, // Changed from PurchaseId to PurchaseOrderId and set to null
                IsActive: true, // Added required field
                CreatedBy: parseInt(userId),
                UpdatedBy: parseInt(userId),
                CreatedDate: new Date().toISOString().split('T')[0], // Format as date only
                UpdatedDate: new Date().toISOString().split('T')[0], // Format as date only
                State:"",
            };

            console.log('Submitting data:', dataToSubmit);
            const response = await postData('SubContractAPI', dataToSubmit, token);
            console.log('Data Created success:', response);

            history.push('/SubContractIndex');
        } catch (error) {
            console.error('Full error object:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            
            // Display more specific error message
            if (error.response && error.response.data) {
                const apiError = error.response.data;
                console.log('API Error details:', apiError);
                if (apiError.message) {
                    setErrorMessage(apiError.message);
                } else if (apiError.errors) {
                    // Handle validation errors
                    const errorMessages = Object.values(apiError.errors).flat().join(', ');
                    setErrorMessage(errorMessages);
                } else if (typeof apiError === 'string') {
                    setErrorMessage(apiError);
                } else {
                    setErrorMessage('An error occurred. Please try again.');
                }
            } else {
                setErrorMessage('An error occurred. Please try again.');
            }
            
            setIsLoading(false);
        }
    };

    const [StateOptions, setstateOptions] = useState([]);
    const [selectedstateType, setSelectedstateType] = useState("");

    const fetchStates = async () => {
        try {

            const response = await fetchData('SubContractAPI', `/State/${companyId}`, token);
            if (response.responseCode === '1111') {

                const options = response.response_data.map(state => ({
                    value: state.Id,
                    label: state.Name
                }));
                console.log('State options loaded:', options);
                setstateOptions(options);
            }
        } catch (error) {
            console.error('Error fetching route:', error);
        }
    };
    const handlestateChange = (selectedOption) => {
        console.log('State selected:', selectedOption);
        setSelectedstateType(selectedOption);
        const stateId = selectedOption ? selectedOption.value : '';
        console.log('Setting StateId to:', stateId);
        setFormData(prevData => ({
            ...prevData,
            StateId: stateId,
            CreatedBy: userId,
            CompanyId: companyId,
        }));
    };

    useEffect(() => {
        fetchStates();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({  
            ...prevData,
            [name]: value
        }));
    };

    return (
        <div className="subcontract-page-wrapper">
            {/* Header with Actions */}
            <div className="subcontract-page-header">
                <div className="header-left">
                    <h1 className="page-title">Create SubContract</h1>
                    <p className="page-subtitle">Add new subcontractor details</p>
                </div>
                <div className="header-actions">
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/SubContractIndex"
                        className="btn-cancel"
                        sx={{ color: '#ffffff !important' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitData}
                        disabled={isLoading}
                        className="btn-save"
                        startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isLoading ? "Saving..." : "Save "}
                    </Button>
                </div>
            </div>

            {/* Error Alert */}
            {errorMessage && (
                <div className="alert-error">
                    <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20" style={{ width: '18px', height: '18px' }}>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Success Alert */}
            {successMessage && (
                <div className="alert-success">
                    <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20" style={{ width: '18px', height: '18px' }}>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{successMessage}</span>
                </div>
            )}

            <div className="subcontract-content">
                {/* Single Unified Section */}
                <div className="form-section">
                    <div className="section-content">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    Name <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="Name"
                                    className="form-control"
                                    value={formData.Name}
                                    onChange={handleChange}
                                    placeholder="Enter subcontractor name"
                                    onClick={(e) => e.target.select()}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Contact Number <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ContactNumber"
                                    className="form-control"
                                    value={formData.ContactNumber}
                                    onChange={handleChange}
                                    placeholder="Enter contact number"
                                    onClick={(e) => e.target.select()}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    State <span className="required">*</span>
                                </label>
                                <Select
                                    classNamePrefix="select"
                                    value={selectedstateType}
                                    onChange={handlestateChange}
                                    options={StateOptions}
                                    placeholder="Select state..."
                                    isSearchable
                                    isClearable
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: "38px",
                                            height: "38px",
                                            fontSize: "13px",
                                            border: "1.5px solid #e2e8f0",
                                            borderRadius: "8px",
                                            background: "#f8fafc",
                                            borderColor: state.isFocused ? '#667eea' : '#e2e8f0',
                                            boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.08)' : 'none',
                                            '&:hover': {
                                                borderColor: '#cbd5e1',
                                                background: '#ffffff',
                                            }
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "38px",
                                            padding: "2px 12px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "38px",
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: "#94a3b8",
                                            fontSize: "12px",
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            color: "#1e293b",
                                            fontSize: "13px",
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            borderRadius: "8px",
                                            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                                            border: "1px solid #e2e8f0",
                                            position: "absolute",
                                        }),
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            fontSize: "13px",
                                            padding: "8px 12px",
                                            background: state.isSelected ? "#667eea" : state.isFocused ? "#f1f5f9" : "white",
                                            color: state.isSelected ? "white" : "#1e293b",
                                        }),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubContractCreate;

